import {useState, useEffect, useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Popover, Modal, Tooltip } from 'react-bootstrap';
import { fileToBase64 } from '../../../../utils/fileToBase64';
import Button from '../../../common/Button';
import { constants } from '../../../common/constants/constants';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { CurrencyValue } from '../../../common/currencyValue';
import BllinkUtils from '../../../common/utils/utils';
import { shortenText, calcMonthsTextForSelectBox, createPdfFromHtml } from '../../../common/shared/statelessMethods';
import arrowLeft from '../../../images/arrow-left.png';
import star from '../../../images/newDesign/star_filled.svg';
import Success from '../../../images/newDesign/success_phone.svg';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { fetchBuildingData } from '../../../store/buildings/actions';
import CommentsModal from '../../comments/CommentsModal';
import WarningLetterModal from '../../notifications/WarningLetterModal';
import { StyledPopover } from '../debt_list/debtList.styled';
import WarningLetter from '../debt_list/warningLetterNew';
import DataModal from '../debt_list/DataModal';
import { StyledDebtModal } from '../debt_list/debtList.styled';
import { ApartmentInfoStyled } from '../tenantList/TenantList.styled';
import Dots from '../tenantList/Dots';
import {ActionsButton} from "../../../common/actions_button/ActionsButton";
import {selectCurrentBuildingCurrency} from "../../../store/buildings/selectors";

const StringUtils = BllinkUtils.Strings;

const AptInfo = ({ apt, buildingID, buildingAddress }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const initialCurrency = useSelector(selectCurrentBuildingCurrency);

    const [showPopover, setShowPopover] = useState(false);
    const [commentsModalOpened, setCommentsModalOpened] = useState(false);
    const [currentApt, setCurrentApt] = useState(apt?.apartmentNumber || '');
    const [selectedRow, setSelectedRow] = useState();
    const [downloadLetter, setDownloadLetter] = useState(false);
    const [sendLetterModal, setSendLetterModal] = useState(false);
    const [contactsNum, setContactsNum] = useState(2);
    const [currency, setCurrency] = useState(initialCurrency);
    const [showDataModal, setShowDataModal] = useState(false);
    const [firstModalContainer, setFirstModalContainer] = useState(true);
    const [selected, setSelected] = useState(1);
    const [monthlyDebt, setMonthlyDebt] = useState({value: '', comment: ''});
    const [oneTimeDebt, setOneTimeDebt] = useState({value: '', comment: ''});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [letterCheckboxes, setLetterCheckboxes] = useState({
        owner: false,
        renter: false
    });
    const [letterInfo, setLetterInfo] = useState({
        address: '',
        fileBase64: ''
    });
    const [currentModalData, setCurrentModalData] = useState({
        tenantData: {},
        aptNumber: '',
        building: ''
    });
    const [tenantInfo, setTenantInfo] = useState({
        OwnerEmail: '',
        OwnerName: '',
        OwnerPhone: '',
        RenterEmail: '',
        RenterName: '',
        RenterPhone: '',
    });

    const getContactContainers = (index)=>{
        const contactsContainers = [
            <div className='contact-info no-border'>
                <img src={star} className='primary-star'/>
                <div>
                    <div className='inner-title'>{t('reports.ownership_type')}</div>
                    <div className='value'>{t(`payments.${constants.APIContract.tenantTypes.renter}`)}</div>
                </div>

                <div>
                    <div className='inner-title'>{t('expenses.name')}</div>
                    <div className='value'>{tenantInfo?.RenterName}</div>
                </div>

                <div>
                    <div className='inner-title'>{t('expenses.phone_number')}</div>
                    <div className='value'>{tenantInfo?.RenterPhone}</div>
                </div>

                <div>
                    <div className='inner-title'>{t('expenses.email')}</div>
                    <div className='value'>{tenantInfo?.RenterEmail}</div>
                </div>
            </div>,
            <div className='contact-info no-border'>
                <img src={star} className='primary-star'/>
                <div>
                    <div className='inner-title'>{t('reports.ownership_type')}</div>
                    <div className='value'>{t(`payments.${constants.APIContract.tenantTypes.owner}`)}</div>
                </div>

                <div>
                    <div className='inner-title'>{t('expenses.name')}</div>
                    <div className='value'>{tenantInfo?.OwnerName}</div>
                </div>

                <div>
                    <div className='inner-title'>{t('expenses.phone_number')}</div>
                    <div className='value'>{tenantInfo?.OwnerPhone}</div>
                </div>

                <div>
                    <div className='inner-title'>{t('expenses.email')}</div>
                    <div className='value'>{tenantInfo?.OwnerEmail}</div>
                </div>
            </div>
        ];
        // set special order of presenting contacts according to task https://bllink.atlassian.net/browse/SSB-48
        if (tenantInfo && !tenantInfo.RenterName && !tenantInfo.RenterPhone && !tenantInfo.RenterEmail
            && (tenantInfo.OwnerName || tenantInfo.OwnerPhone || tenantInfo.OwnerEmail)
        ) {
            contactsContainers.reverse();
        }
        return contactsContainers[index];
    }

    useEffect(async () => {
        if (!currentBuildingData.id || currentBuildingData.id !== buildingID) {
            await dispatch(fetchBuildingData({buildingID}))
        }
        if (currentBuildingData?.currency) setCurrency(currentBuildingData?.currency);
        setTenantInfo(apt?.tenantDetails);
        const monthlyValue = apt?.pastPaymentsLeft;
        let monthlyComment;
        if (apt?.openMonthDebtPayments) monthlyComment = shortenText(calcMonthsTextForSelectBox(apt?.openMonthDebtPayments));
        setMonthlyDebt({value: monthlyValue, comment: monthlyComment});
        const oneTimeValue = apt?.oneTimeLeft;
        let oneTimeComment;
        if (apt?.openOneTimePayments?.length) {
            oneTimeComment = apt.openOneTimePayments.reduce((acc, curVal, index) => `${acc} ${curVal.payment_description}${index < apt.openOneTimePayments.length - 1 ? ',' : ''}`, '');
        }
        setOneTimeDebt({value: oneTimeValue, comment: oneTimeComment}); 
        setCurrentApt(apt?.apartmentNumber);
    }, [apt]);

    useEffect(() => {
        setSelected(1);
    }, [tenantInfo]);

    const apartmentActions = useMemo(() => ([
        { action: 'sendRequest', label: t('reports.debtListActions.sendRequest') },
        { action: 'sendWarningLetter', label: t('reports.debtListActions.sendWarningLetter') },
        { action: 'downloadWarningLetter', label: t('reports.debtListActions.downloadWarningLetter') },
        { action: 'aptNotes', label: t('reports.debtListActions.aptNotes') },
    ]), []);

    function handleApartmentAction(action) {
        switch (action) {
            case 'sendRequest': openDataModal(); break;
            case 'sendWarningLetter': openSendLetterModal(apt); break;
            case 'downloadWarningLetter': downloadWarningLetter(apt); break;
            case 'aptNotes': onAptNotesClick(apt.apartmentNumber, buildingID); break;
            default: throw new Error(`Unexpected action ${action}`);
        }
    }

    const onAddPaymentClick = () => window.open(`/${constants.pages.tenantDetailsManagerUrl}/${buildingID}?apt=${apt?.apartmentNumber}`, '_blank');

    const onAptNotesClick = () => setCommentsModalOpened(true);

    const closeCommentsModal = () => setCommentsModalOpened(false);

    const openDataModal = () => {
        setCurrentModalData({
            tenantData: apt.tenantDetails,
            aptNumber: apt.apartmentNumber,
            building: apt.buildingID
        });
        setShowDataModal(true);
    };

    const hideDataModal = () => {
        setShowDataModal(false);
        setFirstModalContainer(true);
    };

    const openSendLetterModal = row => {
        setSelectedRow(row);
        setSendLetterModal(true);
    };

    const downloadWarningLetter = row => {
        setSelectedRow(row);
        setDownloadLetter(true);
    };

    const hideSendLetterModal = () => {
        setSendLetterModal(false);
        setLetterCheckboxes({
            owner: false,
            renter: false
        });
        setSelectedRow({});
    };

    const sendWarningLetterFromModal = async () => {
        dispatch(showLoader());
        const { address, fileBase64 } = letterInfo;
        const fileName = `${t('letter.fileName')}_${address}_${selectedRow.apartmentNumber}.pdf`;
        const paymentLink = `${constants.frontendUrl}/${constants.pages.paymentSelectionUrl}/${buildingID}/${selectedRow.apartmentNumber}`;
        const { owner, renter } = letterCheckboxes;
        const toOwner = owner;
        const toRenter = renter;
        await client.managers.operations.sendWarningLetter(buildingID, selectedRow.apartmentNumber, paymentLink, 'application/pdf', fileName, fileBase64, toOwner, toRenter);
        setSelectedRow({});
        dispatch(hideLoader());

        hideSendLetterModal();
    };

    const getWarningLetter = async (letterContainer, address) => {
        dispatch(showLoader());

        const file = await createPdfFromHtml(letterContainer, '', {save: false, ignoreLastPage: false});
        const fileBase64 = await fileToBase64(file);
        setLetterInfo({
            address,
            fileBase64
        });

        dispatch(hideLoader());
    };

    const onNextClick = () => {
        if (selected < contactsNum) setSelected(selected+1)
        else return
    };

    const onPrevClick = () => {
        if (selected > 1) setSelected(selected-1)
        else return
    };

    const changeModalContainer = () => setFirstModalContainer(false);

    const onInputChange = e => {
        const targ = e.target;
        setTenantInfo({
            ...tenantInfo,
            [targ.name]: targ.value ? targ.value : ''
        })
    };

    return (
        <ApartmentInfoStyled ltr={ltr} debtList>
            <div className='top-container'>
                <div>{t('payments.number_of_apartment')}: {apt?.apartmentNumber}</div>
                <Button rounded={false} onClick={onAddPaymentClick}>{t('reports.add_payment')}</Button>
            </div>

            <div className='total-contacts'>
                <div>{t('reports.tenant_details')}</div>
                <ActionsButton actions={apartmentActions} onAction={handleApartmentAction} />
            </div>

            {getContactContainers(selected-1)}

            <div className='action-icons padding-bottom'>
                <div className='arrows'>
                    <div className='arrow prev' onClick={onPrevClick}><img src={arrowLeft} /></div>
                    <Dots dotsNum={contactsNum} selected={selected} />
                    <div className='arrow next' onClick={onNextClick}><img src={arrowLeft} /></div>
                </div>
            </div>

            <div className='total-contacts'>{t('reports.debt_details')}</div>
            <div className='contact-info flex'>
                <div>
                    <div className='inner-title'>{t('reports.debt_type')}</div>
                    {monthlyDebt.value !== 0 ? <div className='value nowrap'>{t('reports.monthly_fee')}</div> : ''}
                    {oneTimeDebt.value !== 0 ? <div className='value nowrap'>{t('reports.special_payment')}</div> : ''}
                </div>

                <div>
                    <div className='inner-title'>{t('reports.for')}</div>
                    {monthlyDebt.value !== 0 ? (
                        <OverlayTrigger
                            overlay={props => (
                                <Tooltip {...props}>
                                   {monthlyDebt.comment?.originalComment || monthlyDebt.comment?.shortComment || monthlyDebt.comment}
                                </Tooltip>
                            )}
                        >
                            <div className='value comment'>{monthlyDebt.comment?.shortComment || monthlyDebt.comment}</div>
                        </OverlayTrigger>
                    ) : ''}
                    {oneTimeDebt.value !== 0 ? (
                        <OverlayTrigger
                            overlay={props => (
                                <Tooltip {...props}>
                                   {oneTimeDebt.comment}
                                </Tooltip>
                            )}
                        >
                            <div className='value comment'>{oneTimeDebt.comment}</div>
                        </OverlayTrigger>
                    ) : ''}
                </div>

                <div>
                    <div className='inner-title'>{t('offline_payments.amount')}</div>
                    {monthlyDebt.value !== 0 ? (
                    <div className='value'>
                        <CurrencyValue currency={currency} notInCents>{StringUtils.numberWithCommas(monthlyDebt.value)}</CurrencyValue>
                    </div>) : ''}
                    {oneTimeDebt.value !== 0 ? (
                    <div className='value'>
                        <CurrencyValue currency={currency} notInCents>{StringUtils.numberWithCommas(oneTimeDebt.value)}</CurrencyValue>
                    </div>) : ''}
                </div>
            </div>

            <div className='monthly-fee'>
                <span>{t('reports.total_amount')}:</span>
                <CurrencyValue currency={currency} notInCents>{StringUtils.numberWithCommas(monthlyDebt.value + oneTimeDebt.value)}</CurrencyValue>
            </div>

            <DataModal 
                showDataModal={showDataModal} 
                hideDataModal={hideDataModal} 
                firstModalContainer={firstModalContainer} 
                changeModalContainer={changeModalContainer} 
                tenantInfo={tenantInfo} 
                onInputChange={onInputChange} 
                buildingID={buildingID} 
                setShowSuccessModal={setShowSuccessModal}
                currentModalData={currentModalData}
                buildingAddress={buildingAddress}
                currency={currency}
            />

            <StyledDebtModal
                centered
                show={showSuccessModal}
                onHide={() => setShowSuccessModal(false)}
            >
                <Modal.Body>
                    <img src={Success} alt='success' className='success-img' />

                    <h1>{t('reports.paymentRequestPopUp.thanksTitle')}</h1>
                    <h4>{t('reports.paymentRequestPopUp.requestTitle')}</h4>
                </Modal.Body>
            </StyledDebtModal>

            <CommentsModal
                show={commentsModalOpened}
                buildingId={buildingID}
                onModalHide={closeCommentsModal}
                apartmentNum={currentApt}
                prefetchComments={false}
            />

            <WarningLetterModal
                show={sendLetterModal}
                onHide={hideSendLetterModal}
                checkboxes={letterCheckboxes}
                setCheckboxes={setLetterCheckboxes}
                onSend={sendWarningLetterFromModal}
            />

            {
                selectedRow?.apartmentNumber && (
                    <WarningLetter
                        row={selectedRow}
                        clearSelectedRow={() => setSelectedRow({})}
                        currency={currency}
                        getWarningLetter={getWarningLetter}
                        downloadLetter={downloadLetter}
                        setDownload={setDownloadLetter}
                    />
                )
            }
        </ApartmentInfoStyled>
    )
}

export default AptInfo;
